import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ForceGraph3D from 'react-force-graph-3d';
import { Loader } from '@/components/ui/loader';
import { Compass, ArrowLeftIcon, Info, MousePointer, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchForceGraph, fetchTimelineGraph, fetchPost, fetchSubscription, fetchPostDumpsStatus, type ForceGraphResponse, type ForceGraphTheme, type ForceGraphLinkKind, type TimelineGraphResponse, type Post, type Subscription } from '@/services/api';
import { FullPostPopup } from '@/components/FullPostPopup';
import { Button } from '@/components/ui/button';
import * as THREE from 'three';

// Helper function to calculate point on cubic bezier curve
const getPointOnBezierCurve = (
  t: number,
  p0: { x: number, y: number },
  p1: { x: number, y: number },
  p2: { x: number, y: number },
  p3: { x: number, y: number }
): { x: number, y: number } => {
  const mt = 1 - t;
  const mt2 = mt * mt;
  const mt3 = mt2 * mt;
  const t2 = t * t;
  const t3 = t2 * t;
  
  return {
    x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
    y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y
  };
};

// Helper function to format epoch timestamp (in seconds) to month/year
const formatMonthYear = (epochSeconds: number): string => {
  try {
    // Convert epoch seconds to milliseconds for JavaScript Date
    const date = new Date(epochSeconds * 1000);
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  } catch {
    return String(epochSeconds);
  }
};


// Helper function to draw post card on canvas - redesigned for better UX
const drawPostCard = (context: CanvasRenderingContext2D, thumbnailUrl: string | null | undefined, content: string | null | undefined, author: any, width: number, height: number, clusterColor: string, updateTextureCallback?: () => void) => {
  const leftBarWidth = 10; // Bold, vibrant accent bar
  const borderRadius = 8; // Slightly smaller for modern look
  const borderWidth = 2.5; // Slightly thicker for more visibility
  
  // Clear and draw background - darker for better contrast with bright elements
  context.fillStyle = '#0f172a'; // Deep slate for vibrant contrast
  drawRoundedRect(context, 0, 0, width, height, borderRadius, true, false);
  
  // Draw colored left bar - accent color
  context.fillStyle = clusterColor;
  drawRoundedRect(context, 0, 0, leftBarWidth, height, borderRadius, true, false);
  
  // If thumbnail exists, show it prominently
  if (thumbnailUrl) {
    const img = new Image();
    // Set crossOrigin BEFORE setting src to ensure CORS request
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Verify image loaded successfully before drawing
      if (!img.complete || img.naturalWidth === 0) {
        if (updateTextureCallback) updateTextureCallback();
        return;
      }
      // Redraw background
      context.fillStyle = '#0f172a';
      drawRoundedRect(context, 0, 0, width, height, borderRadius, true, false);
      
      // Redraw left bar
      context.fillStyle = clusterColor;
      drawRoundedRect(context, 0, 0, leftBarWidth, height, borderRadius, true, false);
      
      // Draw thumbnail - use object-fit cover approach
      const imgPadding = leftBarWidth + 12;
      const imgAreaWidth = width - imgPadding - 12;
      const imgAreaHeight = height - 24;
      
      // Calculate dimensions to cover the area (crop to fill)
      const imgAspect = img.width / img.height;
      const areaAspect = imgAreaWidth / imgAreaHeight;
      
      let drawWidth, drawHeight, offsetX, offsetY;
      
      if (imgAspect > areaAspect) {
        // Image is wider - fit height and crop width
        drawHeight = imgAreaHeight;
        drawWidth = drawHeight * imgAspect;
        offsetX = imgPadding + (imgAreaWidth - drawWidth) / 2;
        offsetY = 12;
      } else {
        // Image is taller - fit width and crop height
        drawWidth = imgAreaWidth;
        drawHeight = drawWidth / imgAspect;
        offsetX = imgPadding;
        offsetY = 12 + (imgAreaHeight - drawHeight) / 2;
      }
      
      try {
        // Clip to rounded rectangle and draw - NO OVERLAY for vibrant images
        context.save();
        drawRoundedRect(context, imgPadding, 12, imgAreaWidth, imgAreaHeight, borderRadius - 2, false, false);
        context.clip();
        context.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        context.restore();
        
        // Draw bright border for pop
        context.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        context.lineWidth = borderWidth;
        drawRoundedRect(context, borderWidth / 2, borderWidth / 2, width - borderWidth, height - borderWidth, borderRadius, false, true);
        
        if (updateTextureCallback) updateTextureCallback();
      } catch {
        // Fallback: Draw text-only card on CORS/canvas error
        context.fillStyle = '#0f172a';
        drawRoundedRect(context, 0, 0, width, height, borderRadius, true, false);
        context.fillStyle = clusterColor;
        drawRoundedRect(context, 0, 0, leftBarWidth, height, borderRadius, true, false);
        
        // Draw error placeholder
        context.fillStyle = '#ffffff';
        context.font = '400 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText('🖼️', width / 2, height / 2 - 20);
        context.font = '300 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial';
        context.fillStyle = '#cbd5e1';
        context.fillText('Image not available', width / 2, height / 2 + 10);
        
        context.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        context.lineWidth = borderWidth;
        drawRoundedRect(context, borderWidth / 2, borderWidth / 2, width - borderWidth, height - borderWidth, borderRadius, false, true);
        
        if (updateTextureCallback) updateTextureCallback();
      }
    };
    img.onerror = () => {
      // Fallback: Draw text-only card on load failure
      context.fillStyle = '#0f172a';
      drawRoundedRect(context, 0, 0, width, height, borderRadius, true, false);
      context.fillStyle = clusterColor;
      drawRoundedRect(context, 0, 0, leftBarWidth, height, borderRadius, true, false);
      
      // Draw error placeholder
      context.fillStyle = '#ffffff';
      context.font = '400 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText('🖼️', width / 2, height / 2 - 20);
      context.font = '300 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial';
      context.fillStyle = '#cbd5e1';
      context.fillText('Image unavailable (CORS)', width / 2, height / 2 + 10);
      
      context.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      context.lineWidth = borderWidth;
      drawRoundedRect(context, borderWidth / 2, borderWidth / 2, width - borderWidth, height - borderWidth, borderRadius, false, true);
      
      if (updateTextureCallback) updateTextureCallback();
    };
    
    img.src = thumbnailUrl;
  } else {
    // No thumbnail - beautiful text-based card design
    const mainPadding = 20;
    const leftContentStart = leftBarWidth + mainPadding;
    let currentY = mainPadding;
    
    // Draw author section - just name and username (no avatar)
  if (author && (author.name || author.username)) {
      // Author name - clean typography with bright white
      context.fillStyle = '#ffffff'; // Pure bright white
      context.font = '600 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial';
      context.textAlign = 'left';
      context.textBaseline = 'top';
      const authorName = author.name || author.username || 'Unknown';
      context.fillText(authorName, leftContentStart, currentY);
    
      // Username - still bright but slightly dimmer
      if (author.username && author.username !== author.name) {
        context.fillStyle = '#cbd5e1'; // Bright light gray
        context.font = '400 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial';
        context.fillText(`@${author.username}`, leftContentStart, currentY + 20);
        currentY += 44; // Space after header with username
      } else {
        currentY += 28; // Space after header without username
      }
      
      // Subtle divider line - slightly more visible
      context.strokeStyle = 'rgba(203, 213, 225, 0.2)';
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(leftContentStart, currentY);
      context.lineTo(width - mainPadding, currentY);
      context.stroke();
      
      currentY += 16; // Space after divider
  } else {
      currentY += 8;
  }
  
    // Draw content - main focus with better typography
  if (content) {
      context.fillStyle = '#ffffff'; // Pure bright white for maximum readability
      context.font = '400 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial';
    context.textAlign = 'left';
    context.textBaseline = 'top';
    
      // Better word wrapping with proper spacing
      const maxWidth = width - leftContentStart - mainPadding;
    const words = String(content).split(' ');
    let line = '';
      let y = currentY;
      const lineHeight = 24; // More breathing room for larger text
      const maxLines = Math.min(12, Math.floor((height - currentY - mainPadding - 10) / lineHeight));
    let lineCount = 0;
    
    for (let i = 0; i < words.length && lineCount < maxLines; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = context.measureText(testLine);
      
      if (metrics.width > maxWidth && i > 0) {
          context.fillText(line.trim(), leftContentStart, y);
        line = words[i] + ' ';
        y += lineHeight;
        lineCount++;
      } else {
        line = testLine;
      }
    }
      
      // Draw last line with ellipsis if truncated
      if (line.trim()) {
        if (lineCount >= maxLines && words.length > maxLines * 3) {
          // Truncate with ellipsis
          let truncated = line.trim();
          while (context.measureText(truncated + '...').width > maxWidth && truncated.length > 0) {
            truncated = truncated.slice(0, -1);
          }
          context.fillText(truncated + '...', leftContentStart, y);
  } else {
          context.fillText(line.trim(), leftContentStart, y);
        }
      }
    }
    
    // Draw elegant border - brighter and more visible
    context.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    context.lineWidth = borderWidth;
    drawRoundedRect(context, borderWidth / 2, borderWidth / 2, width - borderWidth, height - borderWidth, borderRadius, false, true);
    
    // Add inner glow effect using the cluster color - vibrant
    context.strokeStyle = hexToRgba(clusterColor, 0.4);
    context.lineWidth = 1.5;
    drawRoundedRect(context, 3, 3, width - 6, height - 6, borderRadius - 1, false, true);
  }
  
  if (updateTextureCallback) updateTextureCallback();
};

// Helper function to draw rounded rectangles
const drawRoundedRect = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fill: boolean,
  stroke: boolean
) => {
  if (width < 2 * radius) radius = width / 2;
  if (height < 2 * radius) radius = height / 2;
  
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
  
  if (fill) {
    context.fill();
  }
  if (stroke) {
    context.stroke();
  }
};

// Helper function to convert hex color to rgba
const hexToRgba = (hex: string, alpha: number): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
  }
  return `rgba(139, 92, 246, ${alpha})`; // Fallback purple
};

const ForceGraphPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState<ForceGraphTheme>('interests_map');
  const [graphData, setGraphData] = useState<ForceGraphResponse | null>(null);
  const [timelineData, setTimelineData] = useState<TimelineGraphResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredNode, setHoveredNode] = useState<any>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showPostPopup, setShowPostPopup] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(true);
  const [totalNodeCount, setTotalNodeCount] = useState(0);
  const graphRef = useRef<any>(null);
  const simulationStoppedRef = useRef<boolean>(false);
  const panStartRef = useRef<{ x: number, y: number } | null>(null);
  const isShiftPressedRef = useRef<boolean>(false);
  const isPanningRef = useRef<boolean>(false);
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const shiftOverlayRef = useRef<HTMLDivElement>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [postDumpsStatus, setPostDumpsStatus] = useState<{ max_posts_limit: number; current_posts_count?: number; unprocessed_post_dumps_count: number } | null>(null);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);

  // Track mouse position for tooltip
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (hoveredNode) {
        setTooltipPosition({ x: e.clientX, y: e.clientY });
      }
    };

    if (hoveredNode) {
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [hoveredNode]);

  // Handle shift key for panning
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift' && !e.repeat) {
        isShiftPressedRef.current = true;
        // Set cursor to grab via DOM (no re-render)
        if (graphContainerRef.current) {
          graphContainerRef.current.style.cursor = 'grab';
        }
        // Show shift overlay via DOM (no re-render)
        if (shiftOverlayRef.current) {
          shiftOverlayRef.current.style.display = 'block';
        }
        // Completely disable orbit controls when shift is pressed
        if (graphRef.current) {
          const controls = graphRef.current.controls();
          if (controls) {
            controls.enabled = false; // Disable the entire OrbitControls
            controls.enableRotate = false;
            controls.enablePan = false;
            controls.enableZoom = false;
          }
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        isShiftPressedRef.current = false;
        isPanningRef.current = false;
        panStartRef.current = null;
        // Reset cursor
        if (graphContainerRef.current) {
          graphContainerRef.current.style.cursor = 'default';
        }
        // Hide shift overlay via DOM (no re-render)
        if (shiftOverlayRef.current) {
          shiftOverlayRef.current.style.display = 'none';
        }
        // Re-enable orbit controls when shift is released
        if (graphRef.current) {
          const controls = graphRef.current.controls();
          if (controls) {
            controls.enabled = true; // Re-enable the entire OrbitControls
            controls.enableRotate = true;
            controls.enablePan = true;
            controls.enableZoom = true;
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Handle mouse events for panning - using refs to avoid stale closure issues
  useEffect(() => {
    if (theme === 'timeline') return;

    const handleMouseDown = (e: MouseEvent) => {
      // Use ref for immediate access to current shift state
      if (isShiftPressedRef.current && e.button === 0) { // Only left mouse button
        isPanningRef.current = true;
        panStartRef.current = { x: e.clientX, y: e.clientY };
        // Update cursor via DOM (no React re-render)
        if (graphContainerRef.current) {
          graphContainerRef.current.style.cursor = 'grabbing';
        }
        // Stop ALL event propagation to prevent OrbitControls from handling this
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      // Use refs for immediate access to current state
      if (isPanningRef.current && panStartRef.current && graphRef.current) {
        const camera = graphRef.current.camera();
        const controls = graphRef.current.controls();
        
        if (camera && controls) {
          const deltaX = e.clientX - panStartRef.current.x;
          const deltaY = e.clientY - panStartRef.current.y;
          
          // Pan speed factor - adjust based on camera distance for consistent feel
          const cameraDistance = camera.position.distanceTo(controls.target);
          const panSpeed = cameraDistance * 0.002;
          
          // Simple 2D panning - move camera and target in world X/Y coordinates
          // Moving mouse right moves view right (camera moves left), hence negative deltaX
          // Moving mouse up moves view up (camera moves up), hence positive deltaY
          const offsetX = -deltaX * panSpeed;
          const offsetY = deltaY * panSpeed;
          
          // Update both camera position and controls target to maintain view direction
          camera.position.x += offsetX;
          camera.position.y += offsetY;
          controls.target.x += offsetX;
          controls.target.y += offsetY;
          controls.update();
          
          panStartRef.current = { x: e.clientX, y: e.clientY };
        }
        // Stop ALL event propagation to prevent OrbitControls from handling this
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (isPanningRef.current) {
        isPanningRef.current = false;
        panStartRef.current = null;
        // Update cursor via DOM (no React re-render) - back to grab since shift is still pressed
        if (graphContainerRef.current && isShiftPressedRef.current) {
          graphContainerRef.current.style.cursor = 'grab';
        } else if (graphContainerRef.current) {
          graphContainerRef.current.style.cursor = 'default';
        }
        // Stop propagation on mouse up as well
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    // Add listeners to window with capture phase to intercept events BEFORE OrbitControls
    window.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('mousemove', handleMouseMove, true);
    window.addEventListener('mouseup', handleMouseUp, true);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('mousemove', handleMouseMove, true);
      window.removeEventListener('mouseup', handleMouseUp, true);
    };
  }, [theme]);

  const themeConfig = {
    interests_map: {
      label: 'Explore by topics',
      description: 'Group posts into semantic clusters (topics)',
    },
    platforms: {
      label: 'Explore by platforms',
      description: 'Show how your behavior differs across platforms',
    },
    timeline: {
      label: 'My posts over time',
      description: 'Show how your saved content evolved through time',
    },
  };

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      // Use different endpoint for timeline
      if (theme === 'timeline') {
        const data = await fetchTimelineGraph();
        if (data && data.groups && Array.isArray(data.groups)) {
          // Count total posts across all groups
          const totalPosts = data.groups.reduce((sum: number, group: any) => 
            sum + (Array.isArray(group.posts) ? group.posts.length : 0), 0);
          setTotalNodeCount(totalPosts);
          setTimelineData(data);
          setGraphData(null);
        } else {
          setTotalNodeCount(0);
          setTimelineData(null);
        }
      } else {
        const data = await fetchForceGraph(theme);
        // Ensure data is valid before setting
        if (data && data.clusters && Array.isArray(data.clusters)) {
          // Validate cluster structure to ensure no error objects
          const validClusters = data.clusters.filter((cluster: any) => {
            if (!cluster || typeof cluster !== 'object') return false;
            // Filter out error objects (they have keys like {type, loc, msg, input, url})
            if (cluster.type || cluster.loc || cluster.msg || cluster.input || cluster.url) return false;
            if (!cluster.cluster_name || typeof cluster.cluster_name !== 'string') return false;
            if (!Array.isArray(cluster.nodes)) return false;
            if (!Array.isArray(cluster.links)) return false;
            
            // Validate nodes - filter out error objects
            const validNodes = cluster.nodes.filter((node: any) => {
              if (!node || typeof node !== 'object') return false;
              // Filter out error objects
              if (node.type || node.loc || node.msg || node.input || node.url) return false;
              return typeof node.id === 'string' && typeof node.node_name === 'string';
            });
            
            // Only keep clusters with valid nodes
            return validNodes.length > 0;
          });
          
          if (validClusters.length > 0) {
            // Count total nodes across all clusters
            const totalNodes = validClusters.reduce((sum: number, cluster: any) => 
              sum + (Array.isArray(cluster.nodes) ? cluster.nodes.length : 0), 0);
            setTotalNodeCount(totalNodes);
            setGraphData({ clusters: validClusters });
            setTimelineData(null);
          } else {
            setTotalNodeCount(0);
            setGraphData(null);
          }
        } else {
          setTotalNodeCount(0);
          setGraphData(null);
        }
      }
    } catch {
      setTotalNodeCount(0);
      setGraphData(null);
      setTimelineData(null);
    } finally {
      setLoading(false);
    }
  }, [theme, isAuthenticated]);

  const handleResetView = useCallback(() => {
    if (!graphRef.current) return;
    
    try {
      const camera = graphRef.current.camera();
      const controls = graphRef.current.controls();
      
      if (camera && controls) {
        // Reset controls target to origin
        controls.target.set(0, 0, 0);
        // Reset camera to front view position
        camera.position.set(0, 0, 300);
        camera.up.set(0, 1, 0);
        camera.lookAt(0, 0, 0);
        controls.update();
      }
      
      graphRef.current.zoomToFit(400, 50);
    } catch {
      // Silently handle reset view errors
    }
  }, []);


  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch subscription and post dumps status for upgrade banner
  useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        const [subscriptionData, postStatus] = await Promise.all([
          fetchSubscription().catch(() => null),
          fetchPostDumpsStatus().catch(() => null),
        ]);
        setSubscription(subscriptionData);
        setPostDumpsStatus(postStatus);
      } catch {
        // Ignore errors
      }
    };
    loadSubscriptionData();
  }, []);

  // Determine if upgrade banner should be shown
  useEffect(() => {
    // Need postDumpsStatus to check unprocessed count
    if (!postDumpsStatus) {
      setShowUpgradeBanner(false);
      return;
    }

    const premiumUntil = localStorage.getItem('premiumUntil');
    const activeSubscriptionStatuses = ['active', 'trialing'];
    
    // No subscription means free plan
    // Check if user has active subscription
    const hasActiveSubscription = subscription && activeSubscriptionStatuses.includes(subscription.status);
    
    // Check if premiumUntil is valid
    const isPremiumUntilValid = premiumUntil &&
      premiumUntil !== 'null' &&
      premiumUntil !== 'undefined' &&
      new Date(premiumUntil) > new Date();
    
    // User is premium only if they have both active subscription AND valid premiumUntil
    const isPremium = hasActiveSubscription && isPremiumUntilValid;

    // Show banner only when:
    // 1. There are unprocessed post dumps (unprocessed_post_dumps_count > 0)
    // 2. User is not premium (including no subscription = free plan)
    const shouldShow = postDumpsStatus.unprocessed_post_dumps_count > 0 && !isPremium;
    setShowUpgradeBanner(shouldShow);
  }, [subscription, postDumpsStatus]);

  // Configure camera controls and lighting for faster, smoother zoom
  useEffect(() => {
    if (!graphData) return;
    
    // Use a small timeout to ensure the graph is fully initialized
    const timer = setTimeout(() => {
      if (graphRef.current) {
        try {
          const controls = graphRef.current.controls();
          if (controls) {
            // Enable smooth damping for smoother camera movement
            controls.enableDamping = true;
            controls.dampingFactor = 0.15; // Increased for smoother, more responsive movement
            // Adjust pan speed for smoother movement
            controls.panSpeed = 1.2;
            // Disable auto-rotate
            controls.autoRotate = false;
            // Enable smooth rotation
            controls.enableRotate = true;
            controls.rotateSpeed = 1.2;
            // Enable zoom with correct direction - ensure zoom works both ways
            controls.enableZoom = true;
            // Set zoom speed to a reasonable value (positive values work correctly)
            controls.zoomSpeed = 1.5;
            // Ensure zoomToFit doesn't interfere with manual zoom
            controls.minDistance = 10;
            controls.maxDistance = 1000;
          }
          
          // Add lighting for PhongMaterial
          const scene = graphRef.current.scene();
          if (scene) {
            // Remove existing lights if any
            const existingLights = scene.children.filter((child: any) => child.isLight);
            existingLights.forEach((light: any) => scene.remove(light));
            
            // Add bright ambient light for vibrant colors
            const ambientLight = new THREE.AmbientLight(0xffffff, 0.9);
            scene.add(ambientLight);
            
            // Add strong directional light from multiple angles
            const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1.0);
            directionalLight1.position.set(10, 10, 10);
            scene.add(directionalLight1);
            
            // Add secondary light from opposite side to reduce shadows
            const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
            directionalLight2.position.set(-10, -10, -10);
            scene.add(directionalLight2);
          }
        } catch {
          // Camera controls configuration failed silently
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [graphData]);


  // Prepare graph data for react-force-graph-3d with cluster spacing - memoized to prevent re-renders
  const graphDataForRender = useMemo(() => {
    if (!graphData) return null;
    return (() => {
    const allNodes: any[] = [];
    const allLinks: any[] = [];
    const clusterMap = new Map<string, { nodes: any[], links: any[], name: string, center: { x: number, y: number, z: number } }>();
    
    // Calculate cluster centers in a circular/spiral pattern for better spacing
    const clusterCount = graphData.clusters.length;
    const angleStep = (2 * Math.PI) / clusterCount;
    const radius = 80; // Reduced distance from center for closer clusters
    
    // Process each cluster and space them apart
    graphData.clusters.forEach((cluster, clusterIndex) => {
      // Calculate cluster center position
      const angle = clusterIndex * angleStep;
      const centerX = Math.cos(angle) * radius;
      const centerY = Math.sin(angle) * radius;
      const centerZ = (clusterIndex % 2 === 0 ? 1 : -1) * 50; // Alternate z for 3D effect
      
      const clusterNodes = cluster.nodes
        .filter((node: any) => {
          // Filter out any invalid nodes or error objects
          // Error objects have keys like {type, loc, msg, input, url}
          if (!node || typeof node !== 'object') return false;
          if (node.type || node.loc || node.msg || node.input || node.url) return false; // Skip error objects
          if (typeof node.id !== 'string' && typeof node.id !== 'number') return false;
          if (typeof node.node_name !== 'string') return false;
          return true;
        })
        .map((node: any) => {
          // Add small random offset within cluster for natural spread
          const offsetX = (Math.random() - 0.5) * 20;
          const offsetY = (Math.random() - 0.5) * 20;
          const offsetZ = (Math.random() - 0.5) * 20;
          
          return {
            id: String(node.id || ''),
            node_name: String(node.node_name || ''),
            thumbnail_url: node.thumbnail_url || null,
            content: node.content || null,
            author: node.author || null,
            cluster: String(cluster.cluster_name || ''),
            clusterId: clusterIndex.toString(),
            platform: String(node.platform || ''),
            // Set initial position near cluster center with small offset
            x: centerX + offsetX,
            y: centerY + offsetY,
            z: centerZ + offsetZ,
          };
        });
      
      // Don't add label nodes - we'll show clusters in legend instead
      
      // Ensure cluster name is a string for the map
      const clusterNameForMap = typeof cluster.cluster_name === 'string' 
        ? cluster.cluster_name 
        : String(cluster.cluster_name || `Cluster ${clusterIndex}`);
      
      clusterMap.set(clusterIndex.toString(), {
        nodes: clusterNodes,
        links: cluster.links,
        name: clusterNameForMap,
        center: { x: centerX, y: centerY, z: centerZ },
      });
      
      allNodes.push(...clusterNodes);
    });
    
    // Create a map for quick node lookup
    const nodeMap = new Map(allNodes.map(node => [node.id, node]));
    
    // Process links from all clusters (exclude label nodes)
    graphData.clusters.forEach((cluster) => {
      // Ensure links is an array
      if (!Array.isArray(cluster.links)) return;
      
      cluster.links.forEach((link: any) => {
        // Filter out error objects from links
        if (link && typeof link === 'object' && (link.type || link.loc || link.msg)) {
          return; // Skip error objects
        }
        
        // Ensure link has source and target
        if (!link || typeof link.source === 'undefined' || typeof link.target === 'undefined') {
          return;
        }
        
        const sourceNode = nodeMap.get(String(link.source));
        const targetNode = nodeMap.get(String(link.target));
        // Only add links between valid nodes
        if (sourceNode && targetNode) {
          allLinks.push({
            source: sourceNode,
            target: targetNode,
            kind: (typeof link.kind === 'string' ? link.kind : 'intra_cluster') as ForceGraphLinkKind,
          });
        }
      });
    });
    
    return { nodes: allNodes, links: allLinks, clusters: Array.from(clusterMap.values()) };
    })();
  }, [graphData]);

  const handleFocusCluster = useCallback((clusterIndex: number) => {
    if (!graphRef.current || !graphDataForRender) return;
    
    // Find the cluster in the clusters array
    const cluster = graphDataForRender.clusters?.[clusterIndex];
    if (!cluster) return;
    
    // Calculate cluster bounds to determine optimal zoom
    const nodes = cluster.nodes;
    if (nodes.length === 0) return;
    
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    
    nodes.forEach((node: any) => {
      if (node.x !== undefined) {
        minX = Math.min(minX, node.x);
        maxX = Math.max(maxX, node.x);
      }
      if (node.y !== undefined) {
        minY = Math.min(minY, node.y);
        maxY = Math.max(maxY, node.y);
      }
      if (node.z !== undefined) {
        minZ = Math.min(minZ, node.z);
        maxZ = Math.max(maxZ, node.z);
      }
    });
    
    // Calculate cluster center
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;
    
    // Calculate cluster dimensions
    const width = maxX - minX;
    const height = maxY - minY;
    const depth = maxZ - minZ;
    const maxDimension = Math.max(width, height, depth);
    
    // Calculate optimal distance to show only this cluster
    // Add some padding (1.5x) to ensure the entire cluster is visible
    const optimalDistance = maxDimension * 1.5;
    // Ensure minimum distance for visibility
    const distance = Math.max(optimalDistance, 50);
    
    // Focus camera on cluster center with optimal zoom
    try {
      const camera = graphRef.current.camera();
      const controls = graphRef.current.controls();
      
      if (camera && controls) {
        // Position camera to show only the cluster
        camera.position.set(
          centerX,
          centerY,
          centerZ + distance
        );
        controls.target.set(centerX, centerY, centerZ);
        controls.update();
      }
    } catch {
      // Focus on cluster failed silently
    }
  }, [graphDataForRender]);

  // Memoized callbacks for ForceGraph3D to prevent re-renders
  const handleNodeClick = useCallback(async (node: any) => {
    // Disable node clicking when shift is pressed (panning mode)
    if (isShiftPressedRef.current) return;
    
    // Get post ID from node
    const postId = node.id || node.post?.id;
    if (!postId) return;
    
    setLoadingPost(true);
    try {
      const post = await fetchPost(Number(postId));
      setSelectedPost(post);
      setShowPostPopup(true);
    } catch {
      // Failed to fetch post
    } finally {
      setLoadingPost(false);
    }
  }, []);

  const handleNodeHover = useCallback((node: any, previousNode: any) => {
    // Disable hover when shift is pressed (panning mode)
    if (isShiftPressedRef.current) return;
    
    // Only allow hover effects if simulation is stopped
    if (!simulationStoppedRef.current) return;
    
    // Aggressively prevent any simulation restart during hover
    if (graphRef.current) {
      // Immediately stop simulation
      try {
        const d3Force = (graphRef.current as any).d3Force;
        if (d3Force) {
          // Completely disable the simulation
          d3Force.stop();
          d3Force.alpha(0);
          d3Force.alphaTarget(0);
          // Remove forces
          d3Force.force('charge', null);
          d3Force.force('link', null);
          d3Force.force('center', null);
        }
      } catch (e) {
        // Ignore errors
      }
      
      const scene = graphRef.current.scene();
      const camera = graphRef.current.camera();
      
      if (scene && camera) {
        // Reset previous node scale
        if (previousNode) {
          const prevBox = scene.getObjectByName(`node-${previousNode.id}`);
          if (prevBox) {
            prevBox.scale.set(1, 1, 1);
          }
        }
        
        // Scale up current node
        if (node) {
          const box = scene.getObjectByName(`node-${node.id}`);
          if (box) {
            box.scale.set(1.5, 1.5, 1.5);
          }
          
          // Update tooltip
          setHoveredNode(node);
        } else {
          // Hide tooltip when not hovering
          setHoveredNode(null);
        }
      }
    }
  }, []);

  // Generate 100 distinct colors using HSL color space for maximum visual distinction
  const clusterColors = useMemo(() => {
    const colors: string[] = [];
    // Use golden ratio to distribute hues evenly
    const goldenRatio = 0.618033988749895;
    let hue = 0;
    
    for (let i = 0; i < 100; i++) {
      // Vary saturation and lightness for more distinction
      const saturation = 65 + (i % 3) * 12; // 65%, 77%, 89%
      const lightness = 50 + (Math.floor(i / 20) % 3) * 8; // 50%, 58%, 66%
      
      // Convert HSL to hex
      const h = hue * 360;
      const s = saturation / 100;
      const l = lightness / 100;
      
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs((h / 60) % 2 - 1));
      const m = l - c / 2;
      
      let r = 0, g = 0, b = 0;
      if (h < 60) { r = c; g = x; b = 0; }
      else if (h < 120) { r = x; g = c; b = 0; }
      else if (h < 180) { r = 0; g = c; b = x; }
      else if (h < 240) { r = 0; g = x; b = c; }
      else if (h < 300) { r = x; g = 0; b = c; }
      else { r = c; g = 0; b = x; }
      
      const toHex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
      colors.push(`#${toHex(r)}${toHex(g)}${toHex(b)}`);
      
      hue = (hue + goldenRatio) % 1;
    }
    
    return colors;
  }, []);

  // Memoized nodeColor callback to prevent re-renders
  const nodeColorCallback = useCallback((node: any) => {
    if (theme === 'platforms') {
      const platformColors: Record<string, string> = {
        'INSTAGRAM': '#E4405F',
        'X': '#000000',
        'LINKEDIN': '#0077B5',
        'TIKTOK': '#000000',
        'REDDIT': '#FF4500',
        'TWITTER': '#1DA1F2',
      };
      const platform = (node.platform || '').toUpperCase();
      return platformColors[platform] || '#8b5cf6';
    } else {
      const clusterId = node.clusterId || '0';
      const colorIndex = parseInt(clusterId.toString()) % clusterColors.length;
      return clusterColors[colorIndex];
    }
  }, [theme, clusterColors]);

  // Memoized nodeThreeObject callback to prevent re-renders
  const nodeThreeObjectCallback = useCallback((node: any) => {
    const boxWidth = 12;
    const boxHeight = 10;
    const boxDepth = 0.8;
    const geometry = new THREE.BoxGeometry(boxWidth, boxHeight, boxDepth);
    const nodeId = String(node.id);
    
    const thumbnailUrl = node.thumbnail_url || null;
    const content = node.content || null;
    const author = node.author || null;
    
    const clusterId = node.clusterId || '0';
    const colorIndex = parseInt(clusterId.toString()) % clusterColors.length;
    const clusterColor = clusterColors[colorIndex];
    
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 500;
    const context = canvas.getContext('2d');
    
    if (!context) {
      const material = new THREE.MeshPhongMaterial({ color: 0x8b5cf6 });
      const box = new THREE.Mesh(geometry, material);
      box.name = `node-${nodeId}`;
      box.userData.node = node;
      return box;
    }
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    texture.flipY = true;
    
    const material = new THREE.MeshPhongMaterial({ 
      map: texture,
      transparent: false,
      side: THREE.DoubleSide,
      emissive: 0x222222,
      emissiveIntensity: 0.2,
      shininess: 30,
      specular: 0x333333,
    });
    
    const box = new THREE.Mesh(geometry, material);
    box.name = `node-${nodeId}`;
    box.userData.node = node;
    box.userData.texture = texture;
    box.userData.canvas = canvas;
    box.userData.context = context;
    box.userData.material = material;
    box.userData.authorImageLoaded = false;
    
    const updateTexture = () => {
      if (box.userData.texture) {
        box.userData.texture.needsUpdate = true;
        if (box.userData.material) {
          box.userData.material.needsUpdate = true;
        }
      }
    };
    
    drawPostCard(context, thumbnailUrl, content, author, canvas.width, canvas.height, clusterColor, updateTexture);
    updateTexture();
    
    setTimeout(() => updateTexture(), 200);
    setTimeout(() => {
      updateTexture();
      if (graphRef.current) {
        try {
          const scene = graphRef.current.scene();
          if (scene) {
            scene.traverse((obj: any) => {
              if (obj === box && obj.material) {
                obj.material.needsUpdate = true;
              }
            });
          }
        } catch (e) {
          // Ignore
        }
      }
    }, 100);
    
    return box;
  }, [clusterColors]);

  // Memoized linkColor callback
  const linkColorCallback = useCallback((link: any) => {
    const kind: ForceGraphLinkKind = (link.kind || 'intra_cluster') as ForceGraphLinkKind;
    const linkColors: Record<ForceGraphLinkKind, string> = {
      'bridge': '#ef4444',
      'cross_cluster': '#f59e0b',
      'timeline': '#3b82f6',
      'platform_bridge': '#10b981',
      'intra_cluster': '#000000',
    };
    return linkColors[kind] || '#000000';
  }, []);

  // Memoized linkWidth callback
  const linkWidthCallback = useCallback((link: any) => {
    const kind = link.kind || 'intra_cluster';
    return (kind === 'bridge' || kind === 'cross_cluster' || kind === 'platform_bridge') ? 2 : 1;
  }, []);

  // Memoized onEngineTick callback
  const onEngineTickCallback = useCallback(() => {
    if (simulationStoppedRef.current && graphRef.current) {
      try {
        const d3Force = (graphRef.current as any).d3Force;
        if (d3Force) {
          d3Force.stop();
          d3Force.alpha(0);
          d3Force.alphaTarget(0);
          d3Force.force('charge', null);
          d3Force.force('link', null);
          d3Force.force('center', null);
          
          const graphData = graphRef.current.getGraphData();
          if (graphData && graphData.nodes) {
            graphData.nodes.forEach((node: any) => {
              if (!node.isLabel && node.x !== undefined) {
                node.fx = node.x;
                node.fy = node.y;
                node.fz = node.z;
              }
            });
          }
        }
      } catch (e) {
        // Ignore errors
      }
    }
  }, []);

  // Memoized onEngineStop callback
  const onEngineStopCallback = useCallback(() => {
    if (graphRef.current && graphData) {
      const hasZoomed = (graphRef.current as any).__hasZoomed;
      if (!hasZoomed) {
        const nodes = graphDataForRender?.nodes || [];
        if (nodes.length > 0) {
          graphRef.current.zoomToFit(800, 50);
        } else {
          graphRef.current.zoomToFit(400, 20);
        }
        (graphRef.current as any).__hasZoomed = true;
        
        setTimeout(() => {
          if (graphRef.current) {
            try {
              const d3Force = (graphRef.current as any).d3Force;
              if (d3Force) {
                d3Force.stop();
                d3Force.alpha(0);
                d3Force.alphaTarget(0);
                d3Force.force('charge', null);
                d3Force.force('link', null);
                d3Force.force('center', null);
                (graphRef.current as any).__simulationStopped = true;
                simulationStoppedRef.current = true;
              }
              
              const currentGraphData = graphRef.current.getGraphData();
              if (currentGraphData && currentGraphData.nodes) {
                currentGraphData.nodes.forEach((node: any) => {
                  if (node.x !== undefined) node.fx = node.x;
                  if (node.y !== undefined) node.fy = node.y;
                  if (node.z !== undefined) node.fz = node.z;
                });
                
                if (d3Force) {
                  d3Force.stop();
                  d3Force.alpha(0);
                  d3Force.alphaTarget(0);
                }
              }
              
              if (typeof graphRef.current.pauseAnimation === 'function') {
                graphRef.current.pauseAnimation();
              }
            } catch {
              // Simulation stop failed silently
            }
          }
        }, 1500);
      }
    }
  }, [graphData, graphDataForRender]);

  // Process timeline data to group by month/year - MUST be before any early returns
  const processedTimelineData = useMemo(() => {
    if (!timelineData || !timelineData.groups) return null;
    
    // Group posts by month/year, keeping track of the epoch timestamp for sorting
    const monthYearGroups = new Map<string, { posts: any[], epochTimestamp: number }>();
    
    timelineData.groups.forEach((group: any) => {
      const monthYear = formatMonthYear(group.timestamp);
      if (!monthYearGroups.has(monthYear)) {
        monthYearGroups.set(monthYear, { posts: [], epochTimestamp: group.timestamp });
      }
      monthYearGroups.get(monthYear)!.posts.push(...group.posts);
    });
    
    // Convert to array format with both display timestamp and epoch for sorting
    const groups = Array.from(monthYearGroups.entries()).map(([displayTimestamp, data]) => ({
      timestamp: displayTimestamp,
      epochTimestamp: data.epochTimestamp,
      posts: data.posts
    }));
    
    // Sort by epoch timestamp (newest first - higher epoch = more recent)
    groups.sort((a, b) => b.epochTimestamp - a.epochTimestamp);
    
    return { groups };
  }, [timelineData]);

  // Upgrade Banner component
  const renderUpgradeBanner = () => {
    if (!showUpgradeBanner || !postDumpsStatus) return null;
    
    return (
      <div className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-r from-purple-600/95 to-pink-600/95 backdrop-blur-sm border-b border-white/20 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white">
              You are only seeing data from your {postDumpsStatus.current_posts_count} saved posts.{' '}
              <span className="font-semibold">Upgrade now</span> to get data from your all posts.
            </div>
          </div>
          <Button
            onClick={() => navigate('/profile?active=subscription')}
            className="bg-white text-purple-600 hover:bg-white/90 font-semibold px-4 py-2 whitespace-nowrap flex-shrink-0"
          >
            Upgrade now
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        {/* Upgrade Banner */}
        {renderUpgradeBanner()}
        {/* Top Header Bar */}
        <div className={`bg-gray-800 border-b border-gray-700 px-6 py-4 ${showUpgradeBanner ? 'mt-12' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-white">Instavault</h1>
              <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-300 border-purple-500/30">
                Force Graph
              </span>
            </div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Loader />
        </div>
      </div>
    );
  }

  // View Mode Toolbar component - floating at top center
  const renderViewModeToolbar = () => (
    <div className={`absolute ${showUpgradeBanner ? 'top-16' : 'top-4'} left-1/2 transform -translate-x-1/2 z-10`}>
      <div className="inline-flex bg-gray-800/95 backdrop-blur-sm rounded-xl p-1.5 border border-gray-700 shadow-lg">
        {(Object.keys(themeConfig) as ForceGraphTheme[]).map((themeKey) => {
          const config = themeConfig[themeKey];
          const isActive = theme === themeKey;
          return (
            <button
              key={themeKey}
              className={`px-5 py-2.5 mx-1 rounded-lg text-sm bg-transparent font-medium transition-all ${
                isActive
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-400 hover:text-white hover:bg-gray-700/50"
              }`}
              onClick={() => setTheme(themeKey)}
              title={config.description}
            >
              {config.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Floating Legend Sidebar - auto-hides when no legends
  const renderLegendOverlay = () => {
    const hasLegends = theme !== 'timeline' && graphDataForRender?.clusters && graphDataForRender.clusters.length > 0;
    
    if (!hasLegends) return null;
    
    return (
      <div 
        className={`absolute ${showUpgradeBanner ? 'top-32' : 'top-20'} left-4 z-10 transition-all duration-300 ${
          isLegendOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
        }`}
      >
        <div className="bg-gray-800/95 backdrop-blur-sm rounded-xl border border-gray-700 shadow-lg w-64 max-h-[calc(100vh-200px)] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <h3 className="text-sm font-semibold text-white">Legends</h3>
            <button 
              onClick={() => setIsLegendOpen(false)}
              className="text-gray-400 bg-transparent hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Legend Items */}
          <div className="p-2 overflow-y-auto flex-1">
            {graphDataForRender?.clusters?.map((cluster, index) => {
              const colorIndex = index % clusterColors.length;
              const color = clusterColors[colorIndex];
              
              return (
                <div
                  key={index}
                  onClick={() => handleFocusCluster(index)}
                  className="w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center space-x-3 text-gray-300 hover:bg-gray-700 hover:text-white cursor-pointer"
                >
                  <div 
                    className="w-3 h-3 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-sm font-medium truncate flex-1">{cluster.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Toggle button for legend when closed
  const renderLegendToggle = () => {
    const hasLegends = theme !== 'timeline' && graphDataForRender?.clusters && graphDataForRender.clusters.length > 0;
    
    if (!hasLegends || isLegendOpen) return null;
    
    return (
      <button
        onClick={() => setIsLegendOpen(true)}
        className={`absolute ${showUpgradeBanner ? 'top-32' : 'top-20'} left-4 z-10 bg-gray-800/95 backdrop-blur-sm rounded-xl border border-gray-700 shadow-lg p-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-all`}
        title="Show Topics"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
    );
  };

  // FAB Reset Button - bottom right
  const renderResetFAB = () => {
    if (theme === 'timeline') return null;
    
    return (
      <button
        onClick={handleResetView}
        className="absolute bottom-24 right-4 z-10 bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110"
        title="Reset View"
      >
        <Compass className="w-5 h-5" />
      </button>
    );
  };

  // Tips Bar - bottom center (hidden for timeline)
  const renderTipsBar = () => {
    if (theme === 'timeline') return null;
    
    return (
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
        <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg px-4 py-2 border border-gray-700 shadow-lg flex items-center gap-6">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <MousePointer className="w-3.5 h-3.5" />
            <span>Hold <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300">Shift</kbd> + drag to pan</span>
          </div>
          <div className="w-px h-4 bg-gray-600" />
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Info className="w-3.5 h-3.5" />
            <span>Scroll to zoom</span>
          </div>
          <div className="w-px h-4 bg-gray-600" />
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>Click node for details</span>
          </div>
        </div>
      </div>
    );
  };

  // Back button - top left
  const renderBackButton = () => (
    <button
      onClick={() => navigate('/')}
      className="absolute top-4 left-4 z-10 bg-gray-800/95 backdrop-blur-sm rounded-lg px-3 py-2 border border-gray-700 shadow-lg flex items-center gap-2 text-gray-300 hover:text-white hover:bg-gray-700 transition-all"
    >
      <ArrowLeftIcon className="w-4 h-4" />
      <span className="text-sm font-medium">Back</span>
    </button>
  );

  // Warning bar for large datasets
  const renderLimitWarning = () => {
    if (totalNodeCount < 1000) return null;
    
    return (
      <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-20">
        <div className="bg-amber-500/40 backdrop-blur-sm rounded-lg px-4 mt-4 py-2 border border-amber-400 shadow-lg flex items-center gap-2 text-white/80">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium">
            Visualisation supports up to 1000 posts. Showing your best 1000 posts.
          </span>
        </div>
      </div>
    );
  };

  // Timeline view - vertical curved timeline with dots
  if (theme === 'timeline') {
    if (!processedTimelineData || !processedTimelineData.groups || processedTimelineData.groups.length === 0) {
      return (
        <div className="min-h-screen h-screen bg-gray-900 overflow-hidden relative">
          {/* Floating UI Elements */}
          {renderUpgradeBanner()}
          {renderBackButton()}
          {renderViewModeToolbar()}
          {renderTipsBar()}
          
          {/* Empty State */}
          <div className="flex items-center justify-center h-full">
            <div className="text-white">No timeline data available</div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen h-screen bg-gray-900 overflow-hidden relative">
        {/* Floating UI Elements */}
        {renderUpgradeBanner()}
        {renderBackButton()}
        {renderViewModeToolbar()}
        {renderLimitWarning()}
        {renderTipsBar()}

        {/* Main Content - Timeline Visualization */}
        <div className="absolute inset-0 overflow-y-auto overflow-x-hidden pt-16">
            <div className="relative w-full flex justify-center py-8">
              <div className="relative" style={{ width: '1000px', minHeight: `${processedTimelineData.groups.length * 180}px` }}>
              {/* SVG Curved Path */}
              <svg 
                className="absolute top-0 left-0 w-full h-full pointer-events-none"
                style={{ zIndex: 0 }}
              >
                <defs>
                  <linearGradient id="pathGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#9333ea', stopOpacity: 1 }} />
                    <stop offset="50%" style={{ stopColor: '#7c3aed', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <path
                  d={(() => {
                    const centerX = 500; // Center of the 1000px container
                    const amplitude = 180; // How far the curve swings left/right
                    const itemHeight = 180; // Spacing between timestamps
                    
                    // Start path at first group position
                    const firstY = 90;
                    let path = `M ${centerX} ${firstY}`;
                    
                    processedTimelineData.groups.forEach((_, index) => {
                      if (index === 0) return; // Skip first, already started
                      
                      const prevY = (index - 1) * itemHeight + 90;
                      const y = index * itemHeight + 90;
                      
                      // Alternate between left and right curves
                      const prevXOffset = (index - 1) % 2 === 0 ? amplitude : -amplitude;
                      const xOffset = index % 2 === 0 ? amplitude : -amplitude;
                      
                      // Smooth cubic bezier curve
                      const controlY1 = prevY + itemHeight * 0.3;
                      const controlY2 = prevY + itemHeight * 0.7;
                      
                      path += ` C ${centerX + prevXOffset} ${controlY1}, ${centerX + xOffset} ${controlY2}, ${centerX + xOffset} ${y}`;
                    });
                    
                    return path;
                  })()}
                  stroke="url(#pathGradient)"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              
              {/* Timestamp groups and dots */}
              {processedTimelineData.groups.map((group, groupIndex) => {
                const centerX = 500; // Center of the 1000px container
                const amplitude = 180;
                const itemHeight = 180;
              
              // Calculate exact point on the curve for this group
              // Match the SVG path generation exactly
              let pointOnCurve = { x: centerX, y: 90 };
              
              if (groupIndex === 0) {
                // First point is at the start of the path: M centerX 90
                pointOnCurve = { x: centerX, y: 90 };
              } else {
                // For each segment, the SVG path uses:
                // C [prevXOffset] [controlY1], [xOffset] [controlY2], [xOffset] [y]
                // Where the current point (p0) is where the previous segment ended
                
                let currentPoint = { x: centerX, y: 90 }; // Start of path
                
                for (let segIndex = 1; segIndex <= groupIndex; segIndex++) {
                  const segPrevY = (segIndex - 1) * itemHeight + 90;
                  const segY = segIndex * itemHeight + 90;
                  const segPrevXOffset = (segIndex - 1) % 2 === 0 ? amplitude : -amplitude;
                  const segXOffset = segIndex % 2 === 0 ? amplitude : -amplitude;
                  
                  // Control points match SVG path exactly
                  const controlY1 = segPrevY + itemHeight * 0.3;
                  const controlY2 = segPrevY + itemHeight * 0.7;
                  
                  // Bezier curve points
                  const p0 = currentPoint; // Current point (where previous segment ended)
                  const p1 = { x: centerX + segPrevXOffset, y: controlY1 };
                  const p2 = { x: centerX + segXOffset, y: controlY2 };
                  const p3 = { x: centerX + segXOffset, y: segY };
                  
                  // Calculate end point of this segment (t=1.0)
                  currentPoint = getPointOnBezierCurve(1.0, p0, p1, p2, p3);
                }
                
                pointOnCurve = currentPoint;
              }
              
              const isLeft = pointOnCurve.x < centerX;
              
              // Pre-calculate bezier control points for spreading dots along the curve
              let segmentP0 = { x: centerX, y: 90 };
              let segmentP1 = { x: centerX, y: 90 };
              let segmentP2 = { x: centerX, y: 90 };
              let segmentP3 = { x: centerX, y: 90 };
              let tMin = 0.7;
              let tMax = 1.0;
              
              if (groupIndex === 0) {
                // For first group, use the FIRST curve segment (from group 0 to group 1)
                // Spread dots from t=0.0 to t=0.3 at the START of the first segment
                const firstSegXOffset = 0 % 2 === 0 ? amplitude : -amplitude; // group 0's offset
                const nextSegXOffset = 1 % 2 === 0 ? amplitude : -amplitude;  // group 1's offset
                const controlY1 = 90 + itemHeight * 0.3;
                const controlY2 = 90 + itemHeight * 0.7;
                const endY = 1 * itemHeight + 90;
                
                segmentP0 = { x: centerX, y: 90 };
                segmentP1 = { x: centerX + firstSegXOffset, y: controlY1 };
                segmentP2 = { x: centerX + nextSegXOffset, y: controlY2 };
                segmentP3 = { x: centerX + nextSegXOffset, y: endY };
                tMin = 0.0;
                tMax = 0.3;
              } else {
                const prevY = (groupIndex - 1) * itemHeight + 90;
                const prevXOffset = (groupIndex - 1) % 2 === 0 ? amplitude : -amplitude;
                const currentXOffset = groupIndex % 2 === 0 ? amplitude : -amplitude;
                
                // Get the start point of this segment
                let segmentStart = { x: centerX, y: 90 };
                if (groupIndex > 1) {
                  const prevPrevY = (groupIndex - 2) * itemHeight + 90;
                  const prevPrevXOffset = (groupIndex - 2) % 2 === 0 ? amplitude : -amplitude;
                  const p0_prev = segmentStart;
                  const p1_prev = { x: centerX + prevPrevXOffset, y: prevPrevY + itemHeight * 0.3 };
                  const p2_prev = { x: centerX + prevXOffset, y: prevPrevY + itemHeight * 0.7 };
                  const p3_prev = { x: centerX + prevXOffset, y: prevY };
                  segmentStart = getPointOnBezierCurve(1.0, p0_prev, p1_prev, p2_prev, p3_prev);
                }
                
                segmentP0 = segmentStart;
                segmentP1 = { x: centerX + prevXOffset, y: prevY + itemHeight * 0.3 };
                segmentP2 = { x: centerX + currentXOffset, y: prevY + itemHeight * 0.7 };
                segmentP3 = { x: centerX + currentXOffset, y: groupIndex * itemHeight + 90 };
                tMin = 0.7;
                tMax = 1.0;
              }
                
                return (
                  <div
                    key={group.timestamp}
                    className="absolute"
                    style={{
                      left: 0,
                      top: 0,
                    }}
                  >
                    {/* Timestamp label - positioned at the main curve point */}
                    <div 
                      className="absolute text-sm font-semibold text-white whitespace-nowrap bg-gray-800/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg border border-gray-700"
                      style={{
                        left: `${pointOnCurve.x + (isLeft ? -50 : 50)}px`,
                        top: `${pointOnCurve.y}px`,
                        transform: `translate(${isLeft ? '-100%' : '0'}, -50%)`,
                        zIndex: 10,
                      }}
                    >
                      {group.timestamp}
                    </div>
                    
                    {/* Dots for posts - each positioned exactly on the curve */}
                    {group.posts.map((post, postIndex: number) => {
                      const postId = String(post.id || postIndex);
                      const isHovered = hoveredNode?.id === post.id;
                      const totalPosts = group.posts.length;
                      
                      // Calculate the exact position on the curve for this dot
                      // Spread dots along the curve by using different t values
                      let dotPosition = { x: pointOnCurve.x, y: pointOnCurve.y };
                      
                      if (totalPosts > 1) {
                        // Spread dots along the bezier segment using pre-calculated tMin and tMax
                        const tValue = tMin + (postIndex / (totalPosts - 1)) * (tMax - tMin);
                        dotPosition = getPointOnBezierCurve(tValue, segmentP0, segmentP1, segmentP2, segmentP3);
                      }
                        
                      return (
                        <div
                          key={postId}
                          className="absolute"
                          style={{
                            left: `${dotPosition.x}px`,
                            top: `${dotPosition.y}px`,
                            transform: 'translate(-50%, -50%)',
                            zIndex: postIndex,
                          }}
                          onMouseEnter={() => setHoveredNode(post)}
                          onMouseLeave={() => setHoveredNode(null)}
                            onClick={async () => {
                              setLoadingPost(true);
                              try {
                                const fetchedPost = await fetchPost(post.id);
                                setSelectedPost(fetchedPost);
                                setShowPostPopup(true);
                              } catch {
                                // Failed to fetch post
                              } finally {
                                setLoadingPost(false);
                              }
                            }}
                          >
                          {/* Hover card */}
                          {isHovered && (
                            <div 
                              className="absolute z-20 w-96 bg-gray-800/95 backdrop-blur-sm border-2 border-purple-500/50 rounded-xl shadow-2xl p-4 pointer-events-none"
                              style={{
                                [dotPosition.x < centerX ? 'left' : 'right']: '30px',
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}
                            >
                              {/* Author info - name and username only */}
                              {post.author && (
                                <div className="mb-3 pb-3 border-b border-gray-700">
                                  <div className="text-sm font-semibold text-white truncate">
                                    {post.author.name}
                                  </div>
                                  {post.author.username && (
                                    <div className="text-xs text-gray-400 truncate">
                                      @{post.author.username}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Content */}
                              {post.content && (
                                <div className="text-sm text-gray-300 line-clamp-4 mb-3">
                                  {post.content}
                                </div>
                              )}
                              
                              {/* Thumbnail */}
                              {post.thumbnail_url && (
                                <img 
                                  src={post.thumbnail_url} 
                                  alt="Post thumbnail"
                                  className="w-full h-40 object-cover rounded-lg mb-2"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                              
                              {/* Platform badge */}
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs font-medium text-purple-300 bg-purple-500/20 px-2 py-1 rounded border border-purple-500/30">
                                  {post.platform}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          {/* Dot - solid vibrant purple */}
                          <div
                            className={`w-4 h-4 rounded-full bg-purple-500 shadow-lg transition-all cursor-pointer ${
                              isHovered ? 'scale-150 ring-4 ring-purple-400/50' : 'hover:scale-125'
                            }`}
                            style={{
                              border: '2px solid #1f2937',
                              boxShadow: '0 0 8px rgba(168, 85, 247, 0.5)',
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        
        {/* Post Popup for Timeline */}
        {showPostPopup && selectedPost && (() => {
          // Extract author based on platform
          let author = null;
          if (selectedPost.platform === 'INSTAGRAM') {
            author = selectedPost.instagram_post?.author;
          } else if (selectedPost.platform === 'X') {
            author = selectedPost.x_post?.author?.user || selectedPost.x_post?.author;
          } else if (selectedPost.platform === 'LINKEDIN') {
            author = selectedPost.linkedin_post?.author;
          } else if (selectedPost.platform === 'TIKTOK') {
            author = selectedPost.tiktok_post?.author;
          } else if (selectedPost.platform === 'REDDIT') {
            author = selectedPost.reddit_post?.author;
          }
          
          return (
            <FullPostPopup
              post={selectedPost}
              author={author}
              platform={selectedPost.platform}
              onClose={() => {
                setShowPostPopup(false);
                setSelectedPost(null);
              }}
            />
          );
        })()}
        
        {/* Loading overlay for post fetch */}
        {loadingPost && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
            <Loader />
          </div>
        )}
      </div>
    );
  }

  // Force graph view for other themes
  if (!graphDataForRender) {
    return (
      <div className="min-h-screen h-screen bg-gray-900 overflow-hidden relative">
        {/* Floating UI Elements */}
        {renderUpgradeBanner()}
        {renderBackButton()}
        {renderViewModeToolbar()}
        {renderTipsBar()}
        
        {/* Empty State */}
        <div className="flex items-center justify-center h-full">
          <div className="text-white">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen h-screen bg-gray-900 overflow-hidden relative">
      {/* Floating UI Elements */}
      {renderUpgradeBanner()}
      {renderBackButton()}
      {renderViewModeToolbar()}
      {renderLimitWarning()}
      {renderLegendOverlay()}
      {renderLegendToggle()}
      {renderResetFAB()}
      {renderTipsBar()}

      {/* Main Content - 3D Force Graph */}
      <div className="absolute inset-0">
          {/* Shift instruction overlay - hidden by default, shown via DOM manipulation */}
          <div 
            ref={shiftOverlayRef}
            className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium"
            style={{ display: 'none' }}
          >
            🖱️ Drag to pan the view
          </div>

          {/* 3D Force Graph */}
          <div 
            ref={graphContainerRef}
            className="absolute inset-0 w-full h-full"
          >
            <ForceGraph3D
              ref={graphRef}
              graphData={graphDataForRender}
              nodeLabel={(node: any) => {
                const label = node.node_name || node.id;
                return typeof label === 'string' ? label : String(label || '');
              }}
              nodeColor={nodeColorCallback}
              nodeThreeObject={nodeThreeObjectCallback}
              onNodeHover={handleNodeHover}
              onNodeClick={handleNodeClick}
              onNodeDrag={() => false}
              linkColor={linkColorCallback}
              linkWidth={linkWidthCallback}
              linkDirectionalArrowLength={0}
              linkDirectionalArrowRelPos={0}
              linkOpacity={0.8}
              enableNavigationControls={true}
              showNavInfo={false}
              cooldownTicks={100}
              d3AlphaDecay={0.1}
              d3VelocityDecay={0.6}
              backgroundColor='#111827'
              linkDirectionalParticles={0}
              linkDirectionalParticleSpeed={0}
              onEngineTick={onEngineTickCallback}
              onEngineStop={onEngineStopCallback}
            />
          </div>

        {/* Tooltip */}
        {hoveredNode && (
          <div
            className="fixed z-50 px-3 py-2 bg-gray-800 border border-gray-700 text-white text-sm rounded-lg shadow-lg pointer-events-none"
            style={{
              left: `${tooltipPosition.x + 10}px`,
              top: `${tooltipPosition.y - 10}px`,
              transform: 'translateY(-100%)',
            }}
          >
            {typeof hoveredNode.node_name === 'string' ? hoveredNode.node_name : (typeof hoveredNode.id === 'string' ? hoveredNode.id : String(hoveredNode.id || ''))}
          </div>
        )}
      </div>
      
      {/* Post Popup */}
      {showPostPopup && selectedPost && (() => {
        // Extract author based on platform
        let author = null;
        if (selectedPost.platform === 'INSTAGRAM') {
          author = selectedPost.instagram_post?.author;
        } else if (selectedPost.platform === 'X') {
          // Try both author.user and author directly
          author = selectedPost.x_post?.author?.user || selectedPost.x_post?.author;
        } else if (selectedPost.platform === 'LINKEDIN') {
          author = selectedPost.linkedin_post?.author;
        } else if (selectedPost.platform === 'TIKTOK') {
          author = selectedPost.tiktok_post?.author;
        } else if (selectedPost.platform === 'REDDIT') {
          author = selectedPost.reddit_post?.author;
        }
        
        return (
        <FullPostPopup
          post={selectedPost}
            author={author}
          platform={selectedPost.platform}
          onClose={() => {
            setShowPostPopup(false);
            setSelectedPost(null);
          }}
        />
        );
      })()}
      
      {/* Loading overlay for post fetch */}
      {loadingPost && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-50">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default ForceGraphPage;

